// Deep-link routing — pure URL <-> view-state helpers, DOM- and state-free.
//
// A chart is shareable as a real path (/d/<id>?dims=…&t=…&add=…&r=…) or the
// legacy hash form (#/d/<id>?…). The two carry the same query grammar. These
// functions parse a location into a Route, and build the share hash back from a
// view snapshot. Render-mode params (embed, theme) are deliberately NOT part of
// the shareable URL — see renderParams() in main.ts.

import type { Selection } from "./meta";

export interface Route {
  id: string;
  dims: string | null;
  series: string | null;
  t: string | null;
  add: string | null;
  title: string | null;
  r: string | null;
  embed: string | null;
}

function routeFromQuery(id: string, qs: string): Route {
  const q = new URLSearchParams(qs);
  return {
    id: decodeURIComponent(id),
    dims: q.get("dims"), series: q.get("series"), t: q.get("t"), add: q.get("add"),
    title: q.get("title"), r: q.get("r"), embed: q.get("embed"),
  };
}

/** Legacy hash route: #/d/<id>?dims=… — null when the hash is not a chart link. */
export function parseHashRoute(hash: string): Route | null {
  const m = (hash || "").match(/^#\/d\/([^?]+)(?:\?(.*))?$/);
  return m ? routeFromQuery(m[1] as string, m[2] || "") : null;
}

/** Path route: /d/<id>?dims=… — the form a server-rendered page boots into.
 * Localized pages live under a language prefix (/de/d/<id>, /fr/…, /it/…);
 * the prefix is consumed here (locale.ts reads it separately for the UI
 * language) and the id/query grammar is identical. */
export function parsePathRoute(pathname: string, search: string): Route | null {
  const m = (pathname || "").match(/^(?:\/(?:de|fr|it))?\/d\/([^/?]+)\/?$/);
  return m ? routeFromQuery(m[1] as string, search || "") : null;
}

/** Path wins over hash (a shared /d/<id> link may still carry an old hash). */
export function parseRoute(pathname: string, search: string, hash: string): Route | null {
  return parsePathRoute(pathname, search) || parseHashRoute(hash);
}

/** "D0=a;D1=x,y" -> { D0: ["a"], D1: ["x","y"] }. */
export function parseDimsStr(dp: string | null | undefined): Selection {
  const sel: Selection = {};
  for (const part of (dp || "").split(";").filter(Boolean)) {
    const i = part.indexOf("=");
    if (i > 0) sel[part.slice(0, i)] = part.slice(i + 1).split(",").filter(Boolean);
  }
  return sel;
}

/** Selection -> "D0=a;D1=x,y", in dim_order, dropping empty dims. */
export function buildDimsParam(dimOrder: string[], selection: Selection): string {
  return (dimOrder || [])
    .filter(d => selection[d] && selection[d].length)
    .map(d => `${d}=${(selection[d] as string[]).join(",")}`)
    .join(";");
}

export type ExtraKind = "ds" | "sub";
export interface ExtraRef { kind: ExtraKind; datasetId: string; code: string }

/** Parse one overlay key: "ds:<id>" or "sub:<id>:<code>". null if neither. */
export function parseExtraKey(key: string): ExtraRef | null {
  if (key.startsWith("sub:")) {
    const rest = key.slice(4), j = rest.lastIndexOf(":");
    if (j < 0) return null;
    return { kind: "sub", datasetId: rest.slice(0, j), code: rest.slice(j + 1) };
  }
  if (key.startsWith("ds:")) return { kind: "ds", datasetId: key.slice(3), code: "" };
  return null;
}

/** epoch-seconds -> ISO date (YYYY-MM-DD). */
export const isoFromEpoch = (s: number): string => new Date(s * 1000).toISOString().slice(0, 10);

export interface ShareState {
  id: string;
  dimsParam: string;
  transform: string;
  /** The dataset's opening transform: t= is written only when the view differs. */
  defaultTransform?: string;
  /** Overlay keys (ds:… / sub:…:…) already in URL form. */
  extras: string[];
  title: string | null;
  /** Visible window as [rawMinSec, rawMaxSec], or null when not zoomed in. */
  window: [number, number] | null;
  /** A range preset in years ("1", "5", "10"): written as r=5y, a window that
   *  follows the latest data. Wins over `window`. */
  preset?: string | null;
}

/** Build the share hash (#/d/<id>?…) from a view snapshot — pure counterpart of
 *  shareHash() in main.ts. The window is emitted only when present (zoomed). */
export function buildShareHash(s: ShareState): string {
  const p = new URLSearchParams();
  if (s.dimsParam) p.set("dims", s.dimsParam);
  if (s.transform && s.transform !== (s.defaultTransform || "level")) p.set("t", s.transform);
  if (s.extras.length) p.set("add", s.extras.join(","));
  if (s.title) p.set("title", s.title);
  if (s.preset) p.set("r", `${s.preset}y`);
  else if (s.window) p.set("r", isoFromEpoch(s.window[0]) + "," + isoFromEpoch(s.window[1]));
  const qs = p.toString();
  return `#/d/${s.id}${qs ? "?" + qs : ""}`;
}

// ---- which view a URL stands for ------------------------------------------
// Every place a visitor can go gets its own address, so Back/Forward, reload and
// a language switch all return to it: a dataset at /d/<id>, the results for a
// text at /?q=<text>, the landing at /, the browse view's Maps tab at
// /datasets/maps. The browse view's Datasets tab has no address of its own (the
// /datasets hub is a server page), so it shares / with the landing and is told
// apart by the history entry's state. /datasets/maps is the SPA shell with a
// server-rendered list of the maps in it (api/discover.R ds_maps_page_html);
// plain /maps is the JSON list.
export type View = "landing" | "results" | "detail";
export type BrowseTab = "datasets" | "maps";
/** `tab` is set only for the Maps tab; absent means the Datasets tab. */
export interface Place { view: View; query: string; tab?: BrowseTab }

/** The home path in a locale: "/" for English, "/de/" etc. otherwise. */
export const homePath = (locale: string): string => (locale === "en" ? "/" : `/${locale}/`);

/** The Maps tab's path in a locale: /datasets/maps, /de/datasets/maps, … */
export const mapsTabPath = (locale: string): string =>
  (locale === "en" ? "" : `/${locale}`) + "/datasets/maps";

const MAPS_TAB_RE = /^(?:\/(?:de|fr|it))?\/datasets\/maps\/?$/;
/** Is this path the Maps tab (in any language)? */
export const isMapsTabPath = (pathname: string): boolean => MAPS_TAB_RE.test(pathname || "");

/** The address of a non-detail view (detail URLs come from buildShareHash).
 * The tab shows only while there is no query, so a query wins. */
export function placeUrl(p: Place, locale: string): string {
  if (p.view === "results" && !p.query && p.tab === "maps") return mapsTabPath(locale);
  const q = p.view === "results" && p.query ? "?" + new URLSearchParams({ q: p.query }).toString() : "";
  return homePath(locale) + q;
}

/** The view a URL stands for. `histState` is history.state, which carries the
 *  view for entries the app pushed; it never overrides a dataset path or the
 *  Maps tab's path. */
export function placeFromUrl(pathname: string, search: string, hash: string, histState: unknown): Place {
  if (parseRoute(pathname, search, hash)) return { view: "detail", query: "" };
  if (isMapsTabPath(pathname)) return { view: "results", query: "", tab: "maps" };
  const hs = histState as Partial<Place> | null;
  if (hs && (hs.view === "results" || hs.view === "landing"))
    return { view: hs.view, query: hs.view === "results" ? hs.query || "" : "" };
  const q = new URLSearchParams(search).get("q") || "";
  return q ? { view: "results", query: q } : { view: "landing", query: "" };
}
